import { useEffect, useState, useCallback } from "react"

import { AIModel } from "@llumiverse/core"
import { ExecutionEnvironment, Interaction } from "@vertesia/common"
import { Play } from "lucide-react"
import { toast } from "sonner"

import { ActionTooltipButton } from "@components/buttons/ActionTooltipButton"
import { GeneratedForm } from "@components/form/GeneratedForm"
import { AgentModelSelection } from "@components/selections/AgentModelSelection"

import { useCompositeState } from "@hooks/use-composite-state"

import FormState from "@plugins/form/FormState"

import { useUserSession } from "@session/UserSession"

interface Config {
    environment: string
    model?: string
}

interface PayloadPanelProps {
    interaction: Interaction | undefined
}
export function PayloadPanel({ interaction }: Readonly<PayloadPanelProps>) {
    const { client } = useUserSession()
    const playgroundState = useCompositeState(FormState)
    const payload = playgroundState.payload
    const [environment, setEnvironment] = useState<ExecutionEnvironment | undefined>(undefined)
    const [formKey] = useState(0)
    const [isRunning, setIsRunning] = useState(false)

    const [localConfig, setLocalConfig] = useState<Config | undefined>(undefined)

    const handleModelChange = useCallback(
        (model: AIModel | undefined) => {
            if (!environment) {
                return
            }

            const newConfig: Config = {
                environment: model?.environment ?? environment.id,
                model: model?.id ?? interaction?.model,
            }

            setLocalConfig(newConfig)

            playgroundState.config = newConfig
        },
        [environment, interaction?.model, playgroundState]
    )

    useEffect(() => {
        if (!client || !interaction) {
            return
        }
        client.environments.retrieve(interaction.environment as string).then(setEnvironment)
    }, [client, interaction])

    useEffect(() => {
        if (!environment) {
            return
        }

        const initialConfig: Config = {
            environment: environment.id,
            model: interaction?.model,
        }

        setLocalConfig(initialConfig)
        playgroundState.config = initialConfig
    }, [environment, interaction?.model, playgroundState])

    useEffect(() => {
        if (localConfig) {
            playgroundState.config = localConfig
        }
    }, [localConfig, playgroundState])

    if (!interaction) {
        return null
    }

    const run = () => {
        setIsRunning(true)
        playgroundState
            .run()
            .catch((error) => {
                toast.error("Error Running Service", {
                    description: error instanceof Error ? error.message : "Unknown error",
                    duration: 4000,
                })
                setIsRunning(false)
            })
            .finally(() => {
                setIsRunning(false)
            })
    }

    return (
        <div className="w-full h-full">
            <div className="flex flex-row justify-between items-center p-2">
                <h4 className="text-xl font-semibold">Service Parameters</h4>
                {!isRunning && <ActionTooltipButton disabled={isRunning} icon={<Play />} variant="outline" tooltip="Run Service" onClick={run} />}
            </div>
            <div className="flex flex-col gap-4 p-2 px-4 min-h-[calc(100vh-166px)] max-h-[calc(100vh-166px)] overflow-auto">
                <AgentModelSelection model={localConfig?.model} onChange={handleModelChange} showSelection />
                <GeneratedForm key={formKey} object={payload} components={{}} />
            </div>
        </div>
    )
}
